import { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";

// Auth Context
import { AuthProvider } from "@/context/AuthContext";

// Components
import Navbar from "@/components/Navbar";
import Hero from "@/components/Hero";
import Services from "@/components/Services";
import About from "@/components/About";
import Partners from "@/components/Partners";
import Recruitment from "@/components/Recruitment";
import Contact from "@/components/Contact";
import Footer from "@/components/Footer";
import ProtectedRoute from "@/components/ProtectedRoute";

// Auth Pages
import Login from "@/pages/Login";
import VerifyEmail from "@/pages/VerifyEmail";
import ForgotPassword from "@/pages/ForgotPassword";
import AuthCallback from "@/pages/AuthCallback";

// Portal Pages
import CustomerDashboard from "@/pages/portal/CustomerDashboard";
import CustomerProfile from "@/pages/portal/CustomerProfile";
import CustomerDocuments from "@/pages/portal/CustomerDocuments";
import CustomerSettings from "@/pages/portal/CustomerSettings";

// Admin Pages
import AdminDashboard from "@/pages/admin/AdminDashboard";
import ApplicationDetail from "@/pages/admin/ApplicationDetail";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HomePage = () => {
  const [services, setServices] = useState([]);
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [servicesRes, partnersRes] = await Promise.all([
          axios.get(`${API}/services`),
          axios.get(`${API}/partners`)
        ]);
        setServices(servicesRes.data.services);
        setPartners(partnersRes.data.partners);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleContactSubmit = async (formData) => {
    try {
      await axios.post(`${API}/contact`, formData);
      toast.success("Thank you! We'll be in touch soon.");
      return true;
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
      return false;
    }
  };

  const handleAgentApply = async (formData) => {
    try {
      await axios.post(`${API}/agent-application`, formData);
      toast.success("Application submitted! We'll contact you shortly.");
      return true;
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
      return false;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center"
        >
          <div className="w-12 h-12 border-4 border-slate-200 border-t-amber-600 rounded-full animate-spin mx-auto mb-4" />
          <p className="text-slate-600 font-medium">Loading...</p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="home-page">
      <Navbar />
      <main>
        <Hero />
        <Services services={services} />
        <About />
        <Partners partners={partners} />
        <Recruitment onApply={handleAgentApply} />
        <Contact onSubmit={handleContactSubmit} />
      </main>
      <Footer />
    </div>
  );
};

// Router component that handles OAuth callback detection
function AppRouter() {
  const location = useLocation();
  
  // Check URL fragment for session_id (OAuth callback)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<HomePage />} />
      <Route path="/login" element={<Login />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/auth/callback" element={<AuthCallback />} />

      {/* Protected Customer Routes */}
      <Route path="/portal/dashboard" element={
        <ProtectedRoute>
          <CustomerDashboard />
        </ProtectedRoute>
      } />
      <Route path="/portal/profile" element={
        <ProtectedRoute>
          <CustomerProfile />
        </ProtectedRoute>
      } />
      <Route path="/portal/documents" element={
        <ProtectedRoute>
          <CustomerDocuments />
        </ProtectedRoute>
      } />
      <Route path="/portal/settings" element={
        <ProtectedRoute>
          <CustomerSettings />
        </ProtectedRoute>
      } />

      {/* Protected Admin Routes */}
      <Route path="/admin" element={
        <ProtectedRoute adminOnly>
          <AdminDashboard />
        </ProtectedRoute>
      } />
      <Route path="/admin/application/:userId" element={
        <ProtectedRoute adminOnly>
          <ApplicationDetail />
        </ProtectedRoute>
      } />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <Toaster position="top-right" richColors />
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
